// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Edit from "@eisinc/XDL/Icons/Edit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
});

interface OwnProps {
    cart?: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    showCutWire?: boolean;
}

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    // XNG-Change
    icon?: IconPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    editableNotesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
        `,
    },
    label: {
        css: css`
            button {
                color: #2e64b0;
                background: none;
                border: none;
                padding: 0;
                font-size: 15px;
                cursor: pointer;
            }
        `,
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable,
    onNotesChange = () => {},
    label,
    extendedStyles,
    showCutWire,
    currentPage,
}) => {
    const [notes, setNotes] = useState(cartLine.notes);
    let cutWire = [] as any;
    let cutWireNotes = "CUTS: ";
    // eslint-disable-next-line no-prototype-builtins
    if (cartLine.properties?.hasOwnProperty("cutWire") && cartLine.properties.cutWire !== "") {
        cutWire = JSON.parse(cartLine.properties.cutWire);
        // eslint-disable-next-line array-callback-return
        cutWire.map((line: any, index: number) => {
            cutWireNotes += `${line.cutCount}*${line.cutValue}/${cartLine.unitOfMeasure}${
                index + 1 !== cutWire.length ? "," : ""
            } `;
        });
    }

    const [showLineNotes, setShowLineNotes] = useState(false);

    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
    };

    const heading = showLineNotes
        ? "Save Product Note"
        : cartLine.notes.length === 0
          ? "Add Product Note"
          : "Edit Product Note";

    React.useEffect(() => {
        if (showCutWire && cutWire.length > 0 && !cartLine.notes.includes(cutWireNotes)) {
            const notes = `${cutWireNotes} ${cartLine.notes}`;
            setNotes(notes);
        }

        // when the user redirects back from quote to cart page, remove the cuts defined from the notes
        if (currentPage.type === "CartPage" && cutWire.length > 0 && cartLine.notes.includes(cutWireNotes)) {
            const notes = cartLine.notes.replace(cutWireNotes, "");
            setNotes(notes.trim());
        }
    }, []);

    React.useEffect(() => {
        if (notes !== cartLine.notes) {
            setNotes(cartLine.notes);
        }
    }, [cartLine.notes]);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        onNotesChange(notes);
        setShowLineNotes(false);
    };

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    const iconSrc = showLineNotes ? Check : Edit;

    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Link onClick={lineNotesClickHandler} {...styles.toggleLink} icon={{ iconProps: { src: iconSrc } }}>
                    {translate(heading)}
                </Link>
                {showLineNotes ? (
                    <TextArea
                        {...styles.editableNotesTextArea}
                        id={`notes-${cartLine?.id}`}
                        disabled={!cart?.canModifyOrder}
                        onChange={changeHandler}
                        onBlur={blurHandler}
                        data-test-selector="cartline_notes"
                        // XNG-Changes
                        placeholder={
                            currentPage.type === "CheckoutReviewAndSubmitPage"
                                ? translate("Add Product Notes here. Request a Certification under Shipping Details.")
                                : translate("Add Product Notes here...")
                        }
                        label=""
                        value={notes}
                        maxLength={4000}
                    />
                ) : (
                    <Typography {...styles.label}>{notes}</Typography>
                )}
            </StyledWrapper>
        );
    }

    // XNG-Change
    if (!notes) {
        return null;
    }

    // XNG-Changes
    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyNotesText}
            heading={translate("Product Note")}
            text={notes}
            data-test-selector="cartline_notes_disabled"
        />
    );
};

export default withCartLine(connect(mapStateToProps)(CartLineNotes));
